import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { BookingMode, TimeLimitType } from '@/lib/timeBookingUtils';

export type LargeGroupContactMethod = 'email' | 'phone';

export interface ReservationSettings {
  id: string;
  bookingTitle: string | null;
  isPaused: boolean;
  pauseMessage: string | null;
  minBookingDays: number;
  maxBookingDays: number;
  minBookingMinutes: number;
  allowSameDayCancellation: boolean;
  minPeople: number;
  maxPeople: number;
  requirePhone: boolean;
  // Booking mode settings
  bookingMode: BookingMode;
  bookingIntervalMinutes: 15 | 30 | 60;
  reservationDurationMinutes: number;
  // Time limit settings (mutually exclusive)
  timeLimitType: TimeLimitType;
  maxReservationsPerTime: number | null;
  maxPeoplePerTime: number | null;
  // Large group contact settings
  largeGroupEmailEnabled: boolean;
  largeGroupEmail: string | null;
  largeGroupContactMethod: LargeGroupContactMethod;
  largeGroupPhone: string | null;
  largeGroupMessage: string | null;
  // Children settings
  childrenEnabled: boolean;
  childrenCapacityMode: 'full' | 'half' | 'none';
  childrenHelpText: string;
  // Stroller settings
  strollerEnabled: boolean;
  strollerHelpText: string;
  // Terrace settings
  terraceEnabled: boolean;
  terraceHelpText: string;
  // Same-day cutoff time (mutually exclusive with minBookingMinutes)
  sameDayCutoffTime: string | null;
  // Custom assignment order
  customAssignmentOrderEnabled: boolean;
  // Marketing consent settings
  emailMarketingEnabled: boolean;
  smsMarketingEnabled: boolean;
  // Bookable zones settings
  zonesEnabled: boolean;
  zonesSelectionMode: 'single' | 'multi';
  zonesHelpText: string;
  zonesStrict: boolean;
  // Coordonnées affichées en cas de blocage (modif/annulation jour même, déjà annulée, lien expiré)
  showPhoneOnBlockedActions: boolean;
  showEmailOnBlockedActions: boolean;
  blockedActionsPhone: string | null;
  blockedActionsEmail: string | null;
}

interface UseReservationSettingsOptions {
  establishmentId?: string;
}

export const useReservationSettings = (options: UseReservationSettingsOptions = {}) => {
  const { establishmentId } = options;
  const [settings, setSettings] = useState<ReservationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      let query = supabase
        .from('reservation_settings')
        .select('*');

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { data, error } = await query.limit(1).single();

      if (error) throw error;
      
      setSettings({
        id: data.id,
        bookingTitle: data.booking_title ?? null,
        isPaused: data.is_paused,
        pauseMessage: data.pause_message,
        minBookingDays: data.min_booking_days,
        maxBookingDays: data.max_booking_days,
        minBookingMinutes: data.min_booking_minutes ?? 0,
        allowSameDayCancellation: data.allow_same_day_cancellation ?? true,
        minPeople: data.min_people ?? 1,
        maxPeople: data.max_people ?? 20,
        requirePhone: data.require_phone ?? false,
        // Booking mode settings with defaults for backward compatibility
        bookingMode: (data.booking_mode as BookingMode) ?? 'slot',
        bookingIntervalMinutes: (data.booking_interval_minutes as 15 | 30 | 60) ?? 15,
        reservationDurationMinutes: data.reservation_duration_minutes ?? 120,
        // Time limit settings
        timeLimitType: (data.time_limit_type as TimeLimitType) ?? 'none',
        maxReservationsPerTime: data.max_reservations_per_time ?? null,
        maxPeoplePerTime: data.max_people_per_time ?? null,
        // Large group contact settings
        largeGroupEmailEnabled: data.large_group_email_enabled ?? false,
        largeGroupEmail: data.large_group_email ?? null,
        largeGroupContactMethod: (data.large_group_contact_method as LargeGroupContactMethod) ?? 'email',
        largeGroupPhone: data.large_group_phone ?? null,
        largeGroupMessage: data.large_group_message ?? null,
        // Children settings
        childrenEnabled: data.children_enabled ?? false,
        childrenCapacityMode: (data.children_capacity_mode as 'full' | 'half' | 'none') ?? 'full',
        childrenHelpText: data.children_help_text ?? 'Indiquez combien de personnes dans votre groupe sont des enfants.',
        // Stroller settings
        strollerEnabled: data.stroller_enabled ?? false,
        strollerHelpText: data.stroller_help_text ?? 'Cochez si vous venez avec une poussette.',
        // Terrace settings
        terraceEnabled: data.terrace_enabled ?? false,
        terraceHelpText: data.terrace_help_text ?? 'Souhaitez-vous être placé en terrasse ? (sous réserve de conditions météorologiques favorables)',
        // Same-day cutoff time
        sameDayCutoffTime: data.same_day_cutoff_time ?? null,
        // Custom assignment order
        customAssignmentOrderEnabled: (data as any).custom_assignment_order_enabled ?? false,
        // Marketing consent settings
        emailMarketingEnabled: (data as any).email_marketing_enabled ?? true,
        smsMarketingEnabled: (data as any).sms_marketing_enabled ?? false,
        // Bookable zones settings
        zonesEnabled: (data as any).zones_enabled ?? false,
        zonesSelectionMode: ((data as any).zones_selection_mode as 'single' | 'multi') ?? 'single',
        zonesHelpText: (data as any).zones_help_text ?? 'Préférez-vous une zone particulière ?',
        zonesStrict: (data as any).zones_strict ?? true,
        showPhoneOnBlockedActions: (data as any).show_phone_on_blocked_actions ?? false,
        showEmailOnBlockedActions: (data as any).show_email_on_blocked_actions ?? false,
        blockedActionsPhone: (data as any).blocked_actions_phone ?? null,
        blockedActionsEmail: (data as any).blocked_actions_email ?? null,
      });
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePause = async () => {
    if (!settings) return;

    try {
      const newPausedState = !settings.isPaused;
      
      const { error } = await supabase
        .from('reservation_settings')
        .update({ is_paused: newPausedState })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, isPaused: newPausedState });
      return newPausedState;
    } catch (error) {
      console.error('Erreur toggle pause:', error);
      throw error;
    }
  };

  const updateBookingRange = async (minDays: number, maxDays: number) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          min_booking_days: minDays,
          max_booking_days: maxDays 
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        minBookingDays: minDays, 
        maxBookingDays: maxDays 
      });
    } catch (error) {
      console.error('Erreur update booking range:', error);
      throw error;
    }
  };

  const updateAllowSameDayCancellation = async (value: boolean) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ allow_same_day_cancellation: value })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, allowSameDayCancellation: value });
    } catch (error) {
      console.error('Erreur update allow_same_day_cancellation:', error);
      throw error;
    }
  };

  const updatePauseWithMessage = async (isPaused: boolean, message: string | null) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          is_paused: isPaused,
          pause_message: message 
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, isPaused, pauseMessage: message });
      return isPaused;
    } catch (error) {
      console.error('Erreur update pause with message:', error);
      throw error;
    }
  };

  const updatePeopleLimits = async (minPeople: number, maxPeople: number) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          min_people: minPeople,
          max_people: maxPeople 
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, minPeople, maxPeople });
    } catch (error) {
      console.error('Erreur update people limits:', error);
      throw error;
    }
  };

  const updateMinBookingMinutes = async (minutes: number) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ min_booking_minutes: minutes })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, minBookingMinutes: minutes });
    } catch (error) {
      console.error('Erreur update min_booking_minutes:', error);
      throw error;
    }
  };

  /**
   * Update booking mode settings (slot vs time mode)
   */
  const updateBookingMode = async (
    bookingMode: BookingMode,
    bookingIntervalMinutes: 15 | 30 | 60,
    reservationDurationMinutes: number,
    timeLimitType: TimeLimitType,
    maxReservationsPerTime: number | null,
    maxPeoplePerTime: number | null
  ) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          booking_mode: bookingMode,
          booking_interval_minutes: bookingIntervalMinutes,
          reservation_duration_minutes: reservationDurationMinutes,
          time_limit_type: timeLimitType,
          max_reservations_per_time: maxReservationsPerTime,
          max_people_per_time: maxPeoplePerTime
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        bookingMode, 
        bookingIntervalMinutes, 
        reservationDurationMinutes,
        timeLimitType,
        maxReservationsPerTime,
        maxPeoplePerTime
      });
    } catch (error) {
      console.error('Erreur update booking mode:', error);
      throw error;
    }
  };

  /**
   * Update large group email settings
   */
  const updateLargeGroupEmail = async (enabled: boolean, email: string | null) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          large_group_email_enabled: enabled,
          large_group_email: email
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        largeGroupEmailEnabled: enabled,
        largeGroupEmail: email
      });
    } catch (error) {
      console.error('Erreur update large group email:', error);
      throw error;
    }
  };

  /**
   * Update large group contact settings (method, phone, message)
   */
  const updateLargeGroupContact = async (
    enabled: boolean,
    contactMethod: LargeGroupContactMethod,
    email: string | null,
    phone: string | null,
    message: string | null
  ) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          large_group_email_enabled: enabled,
          large_group_contact_method: contactMethod,
          large_group_email: contactMethod === 'email' ? email : settings.largeGroupEmail,
          large_group_phone: contactMethod === 'phone' ? phone : settings.largeGroupPhone,
          large_group_message: message || null
        })
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        largeGroupEmailEnabled: enabled,
        largeGroupContactMethod: contactMethod,
        largeGroupEmail: contactMethod === 'email' ? email : settings.largeGroupEmail,
        largeGroupPhone: contactMethod === 'phone' ? phone : settings.largeGroupPhone,
        largeGroupMessage: message || null
      });
    } catch (error) {
      console.error('Erreur update large group contact:', error);
      throw error;
    }
  };

  /**
   * Update children settings
   */
  const updateChildrenSettings = async (enabled: boolean, capacityMode: 'full' | 'half' | 'none', helpText?: string) => {
    if (!settings) return;

    try {
      const updateData: Record<string, unknown> = { 
        children_enabled: enabled,
        children_capacity_mode: capacityMode
      };
      if (helpText !== undefined) {
        updateData.children_help_text = helpText;
      }

      const { error } = await supabase
        .from('reservation_settings')
        .update(updateData)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        childrenEnabled: enabled,
        childrenCapacityMode: capacityMode,
        ...(helpText !== undefined ? { childrenHelpText: helpText } : {}),
      });
    } catch (error) {
      console.error('Erreur update children settings:', error);
      throw error;
    }
  };

  /**
   * Update stroller settings
   */
  const updateStrollerSettings = async (enabled: boolean, helpText?: string) => {
    if (!settings) return;

    try {
      const updateData: Record<string, unknown> = { 
        stroller_enabled: enabled,
      };
      if (helpText !== undefined) {
        updateData.stroller_help_text = helpText;
      }

      const { error } = await supabase
        .from('reservation_settings')
        .update(updateData)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        strollerEnabled: enabled,
        ...(helpText !== undefined ? { strollerHelpText: helpText } : {}),
      });
    } catch (error) {
      console.error('Erreur update stroller settings:', error);
      throw error;
    }
  };

  /**
   * Update same-day cutoff time (mutually exclusive with minBookingMinutes)
   */
  const updateSameDayCutoffTime = async (time: string | null) => {
    if (!settings) return;

    try {
      const updateData: Record<string, unknown> = { 
        same_day_cutoff_time: time,
      };
      // When setting cutoff time, reset min_booking_minutes to 0
      if (time !== null) {
        updateData.min_booking_minutes = 0;
      }

      const { error } = await supabase
        .from('reservation_settings')
        .update(updateData)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        sameDayCutoffTime: time,
        ...(time !== null ? { minBookingMinutes: 0 } : {}),
      });
    } catch (error) {
      console.error('Erreur update same_day_cutoff_time:', error);
      throw error;
    }
  };

  /**
   * Update terrace settings
   */
  const updateTerraceSettings = async (enabled: boolean, helpText?: string) => {
    if (!settings) return;

    try {
      const updateData: Record<string, unknown> = { 
        terrace_enabled: enabled,
      };
      if (helpText !== undefined) {
        updateData.terrace_help_text = helpText;
      }

      const { error } = await supabase
        .from('reservation_settings')
        .update(updateData)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ 
        ...settings, 
        terraceEnabled: enabled,
        ...(helpText !== undefined ? { terraceHelpText: helpText } : {}),
      });
    } catch (error) {
      console.error('Erreur update terrace settings:', error);
      throw error;
    }
  };

  useEffect(() => {
    fetchSettings();

    // Écouter les changements en temps réel
    const channelName = establishmentId 
      ? `reservation_settings_changes_${establishmentId}` 
      : 'reservation_settings_changes';
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'reservation_settings',
          ...(establishmentId ? { filter: `establishment_id=eq.${establishmentId}` } : {}),
        },
        (payload) => {
          setSettings({
            id: payload.new.id,
            bookingTitle: payload.new.booking_title ?? null,
            isPaused: payload.new.is_paused,
            pauseMessage: payload.new.pause_message,
            minBookingDays: payload.new.min_booking_days,
            maxBookingDays: payload.new.max_booking_days,
            minBookingMinutes: payload.new.min_booking_minutes ?? 0,
            allowSameDayCancellation: payload.new.allow_same_day_cancellation ?? true,
            minPeople: payload.new.min_people ?? 1,
            maxPeople: payload.new.max_people ?? 20,
            requirePhone: payload.new.require_phone ?? false,
            // Booking mode settings
            bookingMode: (payload.new.booking_mode as BookingMode) ?? 'slot',
            bookingIntervalMinutes: (payload.new.booking_interval_minutes as 15 | 30 | 60) ?? 15,
            reservationDurationMinutes: payload.new.reservation_duration_minutes ?? 120,
            // Time limit settings
            timeLimitType: (payload.new.time_limit_type as TimeLimitType) ?? 'none',
            maxReservationsPerTime: payload.new.max_reservations_per_time ?? null,
            maxPeoplePerTime: payload.new.max_people_per_time ?? null,
            // Large group contact settings
            largeGroupEmailEnabled: payload.new.large_group_email_enabled ?? false,
            largeGroupEmail: payload.new.large_group_email ?? null,
            largeGroupContactMethod: (payload.new.large_group_contact_method as LargeGroupContactMethod) ?? 'email',
            largeGroupPhone: payload.new.large_group_phone ?? null,
            largeGroupMessage: payload.new.large_group_message ?? null,
            // Children settings
            childrenEnabled: payload.new.children_enabled ?? false,
            childrenCapacityMode: (payload.new.children_capacity_mode as 'full' | 'half' | 'none') ?? 'full',
            childrenHelpText: payload.new.children_help_text ?? 'Indiquez combien de personnes dans votre groupe sont des enfants.',
            // Stroller settings
            strollerEnabled: payload.new.stroller_enabled ?? false,
            strollerHelpText: payload.new.stroller_help_text ?? 'Cochez si vous venez avec une poussette.',
            // Terrace settings
            terraceEnabled: payload.new.terrace_enabled ?? false,
            terraceHelpText: payload.new.terrace_help_text ?? 'Souhaitez-vous être placé en terrasse ? (sous réserve de conditions météorologiques favorables)',
            // Same-day cutoff time
            sameDayCutoffTime: payload.new.same_day_cutoff_time ?? null,
            // Custom assignment order
            customAssignmentOrderEnabled: payload.new.custom_assignment_order_enabled ?? false,
            // Marketing consent settings
            emailMarketingEnabled: (payload.new as any).email_marketing_enabled ?? true,
            smsMarketingEnabled: (payload.new as any).sms_marketing_enabled ?? false,
            // Bookable zones settings
            zonesEnabled: (payload.new as any).zones_enabled ?? false,
            zonesSelectionMode: ((payload.new as any).zones_selection_mode as 'single' | 'multi') ?? 'single',
            zonesHelpText: (payload.new as any).zones_help_text ?? 'Préférez-vous une zone particulière ?',
            zonesStrict: (payload.new as any).zones_strict ?? true,
            showPhoneOnBlockedActions: (payload.new as any).show_phone_on_blocked_actions ?? false,
            showEmailOnBlockedActions: (payload.new as any).show_email_on_blocked_actions ?? false,
            blockedActionsPhone: (payload.new as any).blocked_actions_phone ?? null,
            blockedActionsEmail: (payload.new as any).blocked_actions_email ?? null,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [establishmentId]);

  /**
   * Update custom assignment order enabled
   */
  const updateCustomAssignmentOrder = async (enabled: boolean) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ custom_assignment_order_enabled: enabled } as any)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, customAssignmentOrderEnabled: enabled });
    } catch (error) {
      console.error('Erreur update custom_assignment_order_enabled:', error);
      throw error;
    }
  };

  /**
   * Update marketing consent display settings
   */
  const updateMarketingSettings = async (emailEnabled: boolean, smsEnabled: boolean) => {
    if (!settings) return;

    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ 
          email_marketing_enabled: emailEnabled,
          sms_marketing_enabled: smsEnabled,
        } as any)
        .eq('id', settings.id);

      if (error) throw error;

      setSettings({ ...settings, emailMarketingEnabled: emailEnabled, smsMarketingEnabled: smsEnabled });
    } catch (error) {
      console.error('Erreur update marketing settings:', error);
      throw error;
    }
  };

  /**
   * Update bookable zones settings (enabled, selection mode, help text, strict mode)
   */
  const updateZonesSettings = async (params: {
    enabled?: boolean;
    selectionMode?: 'single' | 'multi';
    helpText?: string;
    strict?: boolean;
  }) => {
    if (!settings) return;
    try {
      const updateData: Record<string, unknown> = {};
      if (params.enabled !== undefined) updateData.zones_enabled = params.enabled;
      if (params.selectionMode !== undefined) updateData.zones_selection_mode = params.selectionMode;
      if (params.helpText !== undefined) updateData.zones_help_text = params.helpText;
      if (params.strict !== undefined) updateData.zones_strict = params.strict;

      const { error } = await supabase
        .from('reservation_settings')
        .update(updateData as any)
        .eq('id', settings.id);
      if (error) throw error;

      setSettings({
        ...settings,
        ...(params.enabled !== undefined ? { zonesEnabled: params.enabled } : {}),
        ...(params.selectionMode !== undefined ? { zonesSelectionMode: params.selectionMode } : {}),
        ...(params.helpText !== undefined ? { zonesHelpText: params.helpText } : {}),
        ...(params.strict !== undefined ? { zonesStrict: params.strict } : {}),
      });
    } catch (error) {
      console.error('Erreur update zones settings:', error);
      throw error;
    }
  };

  const updateBlockedActionsContact = async (
    showPhone: boolean,
    showEmail: boolean,
    phoneValue?: string | null,
    emailValue?: string | null,
  ) => {
    if (!settings) return;
    const nextPhone = phoneValue === undefined ? settings.blockedActionsPhone : (phoneValue && phoneValue.trim() ? phoneValue.trim() : null);
    const nextEmail = emailValue === undefined ? settings.blockedActionsEmail : (emailValue && emailValue.trim() ? emailValue.trim() : null);
    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({
          show_phone_on_blocked_actions: showPhone,
          show_email_on_blocked_actions: showEmail,
          blocked_actions_phone: nextPhone,
          blocked_actions_email: nextEmail,
        } as any)
        .eq('id', settings.id);
      if (error) throw error;
      setSettings({
        ...settings,
        showPhoneOnBlockedActions: showPhone,
        showEmailOnBlockedActions: showEmail,
        blockedActionsPhone: nextPhone,
        blockedActionsEmail: nextEmail,
      });
    } catch (error) {
      console.error('Erreur update blocked actions contact:', error);
      throw error;
    }
  };

  const updateRequirePhone = async (required: boolean) => {
    if (!settings) return;
    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ require_phone: required })
        .eq('id', settings.id);
      if (error) throw error;
      setSettings({ ...settings, requirePhone: required });
    } catch (error) {
      console.error('Erreur update require phone:', error);
      throw error;
    }
  };

  const updateBookingTitle = async (title: string | null) => {
    if (!settings) return;
    const normalizedTitle = title?.trim() || null;
    try {
      const { error } = await supabase
        .from('reservation_settings')
        .update({ booking_title: normalizedTitle })
        .eq('id', settings.id);
      if (error) throw error;
      setSettings({ ...settings, bookingTitle: normalizedTitle });
    } catch (error) {
      console.error('Erreur update booking title:', error);
      throw error;
    }
  };

  return {
    settings, 
    loading, 
    togglePause, 
    updateBookingRange, 
    updateAllowSameDayCancellation, 
    updatePauseWithMessage, 
    updatePeopleLimits, 
    updateMinBookingMinutes,
    updateBookingMode,
    updateLargeGroupEmail,
    updateLargeGroupContact,
    updateChildrenSettings,
    updateStrollerSettings,
    updateSameDayCutoffTime,
    updateTerraceSettings,
    updateCustomAssignmentOrder,
    updateMarketingSettings,
    updateZonesSettings,
    updateBlockedActionsContact,
    updateRequirePhone,
    updateBookingTitle,
    refetch: fetchSettings 
  };
};
